import { APIService } from '../../api/apiService';
import type {
  BatchDetail,
  BatchStats,
  CancelAllFailedBatchesResponse,
  CancelBatchResponse,
  CancelFileJobsPayload,
  CancelFileJobsResponse,
  GetBatchesPayload,
  GetBatchesResponse,
  GetBatchFilesPayload,
  GetBatchFilesResponse,
  RetryFileJobsPayload,
  RetryFileJobsResponse,
} from '../../interfaces/BatchApiService';

export class BatchService extends APIService {
  /**
   * Get list of batch operations with filtering and pagination
   *
   * @param {GetBatchesPayload} payload - The filtering and pagination parameters
   * @return {Promise<GetBatchesResponse>} A promise that resolves with the list of batches
   */
  getBatches(payload: GetBatchesPayload): Promise<GetBatchesResponse> {
    return this.request('/batches', 'POST', { ...payload });
  }

  /**
   * Get batch operation statistics
   *
   * @return {Promise<BatchStats>} A promise that resolves with batch statistics
   */
  getBatchStats(): Promise<BatchStats> {
    return this.request('/batches/stats', 'GET');
  }

  /**
   * Get detailed information about a specific batch
   *
   * @param {number} batchId - The ID of the batch to retrieve
   * @return {Promise<BatchDetail>} A promise that resolves with the batch details
   */
  getBatch(batchId: number): Promise<BatchDetail> {
    return this.request(`/batch/${batchId}`, 'GET');
  }

  /**
   * Cancel a running or queued batch operation
   *
   * @param {number} batchId - The ID of the batch to cancel
   * @return {Promise<void>} A promise that resolves when the batch is cancelled
   */
  cancelBatch(batchId: number): Promise<CancelBatchResponse> {
    return this.request(`/batch/${batchId}`, 'DELETE');
  }

  /**
   * Get files within a specific batch with status details
   *
   * @param {number} batchId - The ID of the batch
   * @param {GetBatchFilesPayload} payload - The filtering and pagination parameters
   * @return {Promise<GetBatchFilesResponse>} A promise that resolves with the list of files in the batch
   */
  getBatchFiles(batchId: number, payload: GetBatchFilesPayload): Promise<GetBatchFilesResponse> {
    return this.request(`/batch/${batchId}/files`, 'POST', { ...payload });
  }

  /**
   * Retry a failed batch operation
   *
   * @param {number} batchId - The ID of the batch to retry
   * @return {Promise<void>} A promise that resolves when the batch retry is initiated
   */
  retryBatch(batchId: number): Promise<void> {
    return this.request(`/batch/${batchId}/retry`, 'POST');
  }

  /**
   * Cancel specific file jobs within a batch
   *
   * @param {CancelFileJobsPayload} payload - The batch ID and file IDs to cancel
   * @return {Promise<CancelFileJobsResponse>} A promise that resolves with the cancellation result
   */
  cancelFileJobs(payload: CancelFileJobsPayload): Promise<CancelFileJobsResponse> {
    return this.request('/batch/files/cancel', 'POST', { ...payload });
  }

  /**
   * Retry specific failed file jobs within a batch
   *
   * @param {RetryFileJobsPayload} payload - The batch ID and file IDs to retry
   * @return {Promise<RetryFileJobsResponse>} A promise that resolves with the retry result
   */
  retryFileJobs(payload: RetryFileJobsPayload): Promise<RetryFileJobsResponse> {
    return this.request('/batch/files/retry', 'POST', { ...payload });
  }

  /**
   * Cancel all failed batches
   *
   * @return {Promise<CancelAllFailedBatchesResponse>} A promise that resolves with the cancellation result
   */
  cancelAllFailedBatches(): Promise<CancelAllFailedBatchesResponse> {
    return this.request('/batches/failed', 'DELETE');
  }
}
